import { useCreativeTemplatesBuilderStore } from 'stores/CreativeTemplatesBuilderStore';
import { getErrorsText, Severity, usePushNotifications } from 'services/NotificationService';
import React, { useEffect, useState } from 'react';
import { AddTextAssetToArticleGroupMutation_addTextAssetToArticleGroup_textAsset } from 'api/kms/articleGroups/__generated__/AddTextAssetToArticleGroupMutation';
import { useAddTextAssetToArticleGroup } from 'api/kms/articleGroups/addTextAssetToArticleGroup.hooks';
import { useCalculatePostMaloneScoreMutation } from 'api/kms/postMaloneScores/calculatePostMaloneScore.hooks';
import { AssetTypeEnum, PostMaloneAssetTypeEnum } from 'api';
import { useDebounce } from 'utils/hooks';
import { useTextAssetsBuilderStore } from '../../../../../../../../../../../stores/TextAssetsBuilderStore';
import { CreativeTemplatesEntities } from '../../../../../../../constants';
import { getTextAssetMaxLengthByTab } from '../../../../../../../utils/textAssetsMaxLength';

export const useTextAssetBuilder = () => {
  const store = useCreativeTemplatesBuilderStore();
  const textAssetsStore = useTextAssetsBuilderStore();

  const activeTabKey = store.activeTab.key;
  const articleGroup = store.articleGroup;
  const postMaloneProject = articleGroup?.postMaloneProject;
  const textAsset = textAssetsStore.getTextAsset(activeTabKey);

  const notify = usePushNotifications();
  const debouncedText = useDebounce(textAsset?.text, 500);
  const [isSaving, changeIsSaving] = useState(false);

  const isShowingPostMaloneScore =
    [CreativeTemplatesEntities.PostTexts, CreativeTemplatesEntities.Headlines].includes(activeTabKey) &&
    textAsset &&
    postMaloneProject;

  //
  // Saving Text Asset
  //

  const onStartSaving = () => {
    changeIsSaving(true);
  };

  const onCompletedSaving = (newTextAsset: AddTextAssetToArticleGroupMutation_addTextAssetToArticleGroup_textAsset) => {
    changeIsSaving(false);
    handleRemoveAsset();

    store.addTextAsset(activeTabKey, {
      ...newTextAsset,
      isBlank: false,
      isAutofilled: false
    });
    store.selectTextAsset(newTextAsset);
  };

  const onErrorSaving = (errors: string[]) => {
    changeIsSaving(false);

    notify({
      title: 'Failed to add Text Asset',
      message: getErrorsText(errors),
      severity: Severity.ERROR
    });
  };

  const addTextAssetToArticleGroup = useAddTextAssetToArticleGroup({
    onStart: onStartSaving,
    onCompleted: onCompletedSaving,
    onError: onErrorSaving
  });

  //
  // Calculating Post Malone Score
  //

  const onCompletedCalculating = (engagementScore: number) => {
    textAssetsStore.changeEngagementScore(activeTabKey, engagementScore);
  };

  const onErrorCalculating = (errors: string[]) => {
    notify({
      title: 'Failed to get a score',
      message: getErrorsText(errors),
      severity: Severity.ERROR
    });
  };

  const [calculateTextAssetScore, { loading: isCalculating }] = useCalculatePostMaloneScoreMutation({
    onCompleted: onCompletedCalculating,
    onError: onErrorCalculating
  });

  useEffect(() => {
    if (!postMaloneProject || !debouncedText) return;

    const assetType = getPostMaloneAssetType();
    if (!assetType) return;

    calculateTextAssetScore({
      externalProjectId: postMaloneProject.externalProjectId.toString(),
      text: debouncedText,
      assetType: assetType
    });
  }, [debouncedText]);

  //
  // Handlers
  //

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!textAsset) return;

    textAssetsStore.changeText(activeTabKey, event.target.value);
  };

  const handleSaveAsset = () => {
    if (!articleGroup?.id || !textAsset) return;

    const assetType = getAssetType();
    if (!assetType) return;

    addTextAssetToArticleGroup({
      articleGroupId: articleGroup.id.toString(),
      text: textAsset.text,
      assetType: assetType,
      engagementScore: textAsset.postMaloneScore?.engagementScore,
      postMaloneProjectId: postMaloneProject?.id?.toString()
    });
  };

  const handleRemoveAsset = () => {
    textAssetsStore.remove(activeTabKey);
  };

  //
  // Utils functions
  //

  const getAssetType = () => {
    switch (activeTabKey) {
      case CreativeTemplatesEntities.Headlines:
        return AssetTypeEnum.headline;
      case CreativeTemplatesEntities.PostTexts:
        return AssetTypeEnum.post_text;
      case CreativeTemplatesEntities.Descriptions:
        return AssetTypeEnum.description;
    }
  };

  const getPostMaloneAssetType = () => {
    switch (activeTabKey) {
      case CreativeTemplatesEntities.Headlines:
        return PostMaloneAssetTypeEnum.facebookHeadline;
      case CreativeTemplatesEntities.PostTexts:
        return PostMaloneAssetTypeEnum.facebookPrimaryText;
    }
  };

  const maxLengthLabelAttrs = () => {
    if (!textAsset) return;

    const maxTextLength = getTextAssetMaxLengthByTab(activeTabKey);

    return {
      helperText: `${textAsset.text.length}/${maxTextLength}`,
      inputProps: { maxLength: maxTextLength }
    };
  };

  return {
    textAsset,
    postMaloneProject,
    isShowingPostMaloneScore,
    isSaving,
    isCalculating,
    handleInputChange,
    handleSaveAsset,
    handleRemoveAsset,
    maxLengthLabelAttrs
  };
};
